import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Gift, Pencil, Plus, Trash2, X } from 'lucide-react';
import type {
  CreatePrizeCategoryInput,
  PrizeCategoryDto,
  PrizeItemDto,
  UpdatePrizeItemInput,
} from '@gc/shared';

import { api } from '@/lib/api';
import {
  Field,
  ImageUpload,
  Toggle,
  ghostPillClass,
  inputClass,
  pillClass,
} from '@/features/settings/controls';
import { toFieldErrors, useResource, type FieldError } from '@/features/settings/useSettings';
import {
  PRIZES_PATH,
  PRIZE_CATEGORIES_PATH,
  prizeFormFromDto,
  toPrizeItemPayload,
  toPrizeItemUpdatePayload,
  type PrizeFormState,
} from './engagement';

/**
 * Prize Vault (SPEC §9) — the catalogue customers spend points on.
 *
 * Two panes: categories (name + sort order, inline CRUD) and the prizes table.
 * Deleting a category never deletes its prizes — they just fall back to
 * uncategorised — and the confirm copy says so.
 */
export function PrizeVaultPage() {
  const { t } = useTranslation();

  const categoriesResource = useResource<PrizeCategoryDto[]>(PRIZE_CATEGORIES_PATH);
  const prizesResource = useResource<PrizeItemDto[]>(PRIZES_PATH);
  const categories = categoriesResource.data ?? [];
  const prizes = prizesResource.data ?? [];

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PrizeItemDto | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteRefused, setDeleteRefused] = useState<{
    prize: PrizeItemDto;
    message: string;
  } | null>(null);

  // Category changes shift prizeCount; prize changes shift pendingCount — the
  // cheap answer is to refresh both panes together.
  const reloadAll = async () => {
    await Promise.all([categoriesResource.reload(), prizesResource.reload()]);
  };

  const categoryName = (id: string | null) =>
    id === null
      ? t('portalContent.prizes.noCategory')
      : (categories.find((category) => category.id === id)?.name ??
        t('portalContent.prizes.noCategory'));

  async function handleToggle(prize: PrizeItemDto, isActive: boolean): Promise<void> {
    setTogglingId(prize.id);
    setError(null);
    try {
      await api.patch(`${PRIZES_PATH}/${prize.id}`, { isActive } satisfies UpdatePrizeItemInput);
      await prizesResource.reload();
    } catch (caught) {
      setError(toFieldErrors(caught, t('portalContent.prizes.saveFailed'))[0]?.message ?? null);
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(prize: PrizeItemDto): Promise<void> {
    if (!window.confirm(t('portalContent.prizes.confirmDelete', { name: prize.name }))) return;
    setError(null);
    try {
      await api.delete(`${PRIZES_PATH}/${prize.id}`);
      setDeleteRefused(null);
      await reloadAll();
    } catch (caught) {
      const message =
        toFieldErrors(caught, t('portalContent.prizes.deleteFailed'))[0]?.message ??
        t('portalContent.prizes.deleteFailed');
      setDeleteRefused({ prize, message });
    }
  }

  async function handleDeactivate(prize: PrizeItemDto): Promise<void> {
    try {
      await api.patch(`${PRIZES_PATH}/${prize.id}`, {
        isActive: false,
      } satisfies UpdatePrizeItemInput);
      setDeleteRefused(null);
      await prizesResource.reload();
    } catch (caught) {
      const message =
        toFieldErrors(caught, t('portalContent.prizes.deactivateFailed'))[0]?.message ??
        t('portalContent.prizes.deactivateFailed');
      setDeleteRefused({ prize, message });
    }
  }

  return (
    <div className="flex flex-wrap items-start gap-4 p-6">
      <CategoriesPane
        categories={categories}
        loading={categoriesResource.loading}
        loadError={categoriesResource.error}
        onChanged={() => void reloadAll()}
      />

      <section className="min-w-0 flex-1">
        <header className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-content">{t('portalContent.prizes.title')}</h2>

          <div className="flex-1" />

          <button type="button" onClick={() => setCreating(true)} className={pillClass}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-4 w-4" aria-hidden />
              {t('portalContent.prizes.addNew')}
            </span>
          </button>
        </header>

        {error ? (
          <p role="alert" className="mb-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        {deleteRefused ? (
          <div
            role="alert"
            className="mb-3 flex flex-wrap items-center gap-3 rounded-card border border-subtle bg-raised p-3"
          >
            <p className="text-sm text-danger">{deleteRefused.message}</p>
            <div className="flex-1" />
            {deleteRefused.prize.isActive ? (
              <button
                type="button"
                onClick={() => void handleDeactivate(deleteRefused.prize)}
                className={ghostPillClass}
              >
                {t('portalContent.deactivateInstead')}
              </button>
            ) : null}
            <button type="button" onClick={() => setDeleteRefused(null)} className={ghostPillClass}>
              {t('portalContent.dismiss')}
            </button>
          </div>
        ) : null}

        {prizesResource.loading && prizes.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : prizesResource.error ? (
          // Before the empty state: a failed GET must never read as an empty vault.
          <p className="text-sm text-danger">{prizesResource.error}</p>
        ) : prizes.length === 0 ? (
          <p className="text-sm text-content-muted">{t('portalContent.prizes.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max border-collapse text-sm">
              <thead>
                <tr className="border-b border-subtle">
                  <Th>
                    <span className="sr-only">{t('portalContent.prizes.columns.image')}</span>
                  </Th>
                  <Th>{t('portalContent.prizes.columns.name')}</Th>
                  <Th>{t('portalContent.prizes.columns.category')}</Th>
                  <Th>{t('portalContent.prizes.columns.cost')}</Th>
                  <Th>{t('portalContent.prizes.columns.stock')}</Th>
                  <Th>{t('portalContent.prizes.columns.active')}</Th>
                  <Th>{t('portalContent.prizes.columns.pending')}</Th>
                  <Th>
                    <span className="sr-only">{t('common.actions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {prizes.map((prize, index) => (
                  <tr
                    key={prize.id}
                    className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
                  >
                    <Td>
                      <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-control bg-input">
                        {prize.imageUrl ? (
                          <img src={prize.imageUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Gift className="h-4 w-4 text-content-muted" aria-hidden />
                        )}
                      </div>
                    </Td>
                    <Td>
                      <p className="font-medium text-content">{prize.name}</p>
                      {prize.description ? (
                        <p className="max-w-64 truncate text-xs text-content-muted">
                          {prize.description}
                        </p>
                      ) : null}
                    </Td>
                    <Td>
                      <span
                        className={
                          prize.prizeCategoryId === null ? 'text-content-muted' : undefined
                        }
                      >
                        {categoryName(prize.prizeCategoryId)}
                      </span>
                    </Td>
                    <Td>
                      <span className="tabular">{prize.pointsCost}</span>{' '}
                      <span className="text-content-muted">{t('portalContent.pointsShort')}</span>
                    </Td>
                    <Td>
                      {prize.stock === null ? (
                        <span
                          title={t('portalContent.prizes.unlimited')}
                          aria-label={t('portalContent.prizes.unlimited')}
                        >
                          ∞
                        </span>
                      ) : (
                        <span className="tabular">{prize.stock}</span>
                      )}
                    </Td>
                    <Td>
                      <input
                        type="checkbox"
                        checked={prize.isActive}
                        disabled={togglingId === prize.id}
                        aria-label={t('portalContent.prizes.toggleActive', { name: prize.name })}
                        onChange={(event) => void handleToggle(prize, event.target.checked)}
                        className="h-4 w-4 accent-[var(--brand-primary)]"
                      />
                    </Td>
                    <Td>
                      {prize.pendingCount > 0 ? (
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-primary">
                          {t('portalContent.prizes.pendingBadge', { count: prize.pendingCount })}
                        </span>
                      ) : (
                        <span className="text-content-muted">—</span>
                      )}
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(prize)}
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                          className="grid h-7 w-7 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(prize)}
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                          className="grid h-7 w-7 place-items-center rounded-control text-danger hover:bg-hover"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating ? (
        <PrizeDrawer
          prize={null}
          categories={categories}
          onClose={() => setCreating(false)}
          onSaved={() => void reloadAll()}
        />
      ) : null}

      {editing ? (
        <PrizeDrawer
          prize={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => void reloadAll()}
        />
      ) : null}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-content-muted"
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap px-3 py-2 text-content">{children}</td>;
}

/**
 * Categories: name + sort order, edited inline. Small enough that a drawer
 * would be ceremony — the row IS the form.
 */
function CategoriesPane({
  categories,
  loading,
  loadError,
  onChanged,
}: {
  categories: PrizeCategoryDto[];
  loading: boolean;
  loadError: string | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();

  /** null = browsing, 'new' = the add row, otherwise the id being edited. */
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftSort, setDraftSort] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function startEdit(category: PrizeCategoryDto | null): void {
    setEditingId(category?.id ?? 'new');
    setDraftName(category?.name ?? '');
    setDraftSort(String(category?.sortOrder ?? 0));
    setError(null);
  }

  async function handleSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const payload: CreatePrizeCategoryInput = {
      name: draftName,
      sortOrder: Number(draftSort.trim() || 0),
    };
    try {
      if (editingId === 'new') {
        await api.post(PRIZE_CATEGORIES_PATH, payload);
      } else {
        await api.patch(`${PRIZE_CATEGORIES_PATH}/${editingId}`, payload);
      }
      setEditingId(null);
      onChanged();
    } catch (caught) {
      setError(
        toFieldErrors(caught, t('portalContent.categories.saveFailed'))[0]?.message ??
          t('portalContent.categories.saveFailed'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(category: PrizeCategoryDto): Promise<void> {
    if (!window.confirm(t('portalContent.categories.confirmDelete', { name: category.name }))) {
      return;
    }
    setError(null);
    try {
      await api.delete(`${PRIZE_CATEGORIES_PATH}/${category.id}`);
      onChanged();
    } catch (caught) {
      setError(
        toFieldErrors(caught, t('portalContent.categories.deleteFailed'))[0]?.message ??
          t('portalContent.categories.deleteFailed'),
      );
    }
  }

  const editRow = (
    <form onSubmit={(event) => void handleSave(event)} className="flex items-center gap-2">
      <input
        value={draftName}
        onChange={(event) => setDraftName(event.target.value)}
        required
        maxLength={60}
        placeholder={t('common.name')}
        autoFocus
        className={`${inputClass} min-w-0 flex-1 px-2 py-1 text-sm`}
      />
      <input
        type="number"
        value={draftSort}
        onChange={(event) => setDraftSort(event.target.value)}
        min={0}
        max={9_999}
        aria-label={t('portalContent.categories.sortOrder')}
        title={t('portalContent.categories.sortOrder')}
        className={`${inputClass} w-16 px-2 py-1 text-sm tabular`}
      />
      <button
        type="submit"
        disabled={saving}
        title={t('common.save')}
        aria-label={t('common.save')}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-control text-primary hover:bg-hover disabled:opacity-40"
      >
        <Check className="h-4 w-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setEditingId(null)}
        title={t('common.cancel')}
        aria-label={t('common.cancel')}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-control text-content-secondary hover:bg-hover"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </form>
  );

  return (
    <section className="w-80 shrink-0 rounded-card border border-subtle bg-raised p-4">
      <header className="mb-3 flex items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold uppercase tracking-wide text-content-secondary">
          {t('portalContent.categories.title')}
        </h2>
        <button
          type="button"
          onClick={() => startEdit(null)}
          title={t('portalContent.categories.add')}
          aria-label={t('portalContent.categories.add')}
          className="grid h-7 w-7 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-content"
        >
          <Plus className="h-4 w-4" aria-hidden />
        </button>
      </header>

      {error ? (
        <p role="alert" className="mb-2 text-xs text-danger">
          {error}
        </p>
      ) : null}

      {loading && categories.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : loadError && categories.length === 0 ? (
        // Before the empty state: a failed GET must never read as "no categories".
        <p className="text-sm text-danger">{loadError}</p>
      ) : categories.length === 0 && editingId !== 'new' ? (
        <p className="text-sm text-content-muted">{t('portalContent.categories.empty')}</p>
      ) : null}

      <ul className="space-y-1">
        {categories.map((category) => (
          <li key={category.id} className="rounded-control px-1 py-1">
            {editingId === category.id ? (
              editRow
            ) : (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">{category.name}</p>
                  <p className="text-xs text-content-muted">
                    {t('portalContent.categories.prizeCount', { count: category.prizeCount })}
                    {' · '}
                    {t('portalContent.categories.sortOrder')}{' '}
                    <span className="tabular">{category.sortOrder}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => startEdit(category)}
                  title={t('common.edit')}
                  aria-label={t('common.edit')}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(category)}
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-control text-danger hover:bg-hover"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
            )}
          </li>
        ))}
        {editingId === 'new' ? <li className="rounded-control px-1 py-1">{editRow}</li> : null}
      </ul>
    </section>
  );
}

/** Add/edit a prize — every field of `createPrizeItemSchema`. */
function PrizeDrawer({
  prize,
  categories,
  onClose,
  onSaved,
}: {
  prize: PrizeItemDto | null;
  categories: PrizeCategoryDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = prize !== null;

  // `prizeFormFromDto` is also the baseline `toPrizeItemUpdatePayload` diffs
  // against, which is what guarantees an untouched field is never sent.
  const [form, setForm] = useState<PrizeFormState>(() => prizeFormFromDto(prize));
  const [imageUrl, setImageUrl] = useState<string | null>(prize?.imageUrl ?? null);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof PrizeFormState>(key: K, value: PrizeFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    try {
      if (isEdit && prize) {
        // Diffed against what the drawer loaded. Above all this keeps a stale
        // `stock` off the wire: customers decrement it by redeeming while the
        // drawer sits open, and a full payload wrote the old count back.
        await api.patch(`${PRIZES_PATH}/${prize.id}`, toPrizeItemUpdatePayload(form, prize));
      } else {
        await api.post(PRIZES_PATH, toPrizeItemPayload(form));
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('portalContent.prizes.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('portalContent.prizes.edit') : t('portalContent.prizes.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-5">
          <ImageUpload
            label={t('portalContent.prizes.fields.image')}
            kind="PRIZE_IMAGE"
            currentUrl={imageUrl}
            onUploaded={(assetId, url) => {
              set('imageMediaAssetId', assetId);
              setImageUrl(url);
            }}
            onCleared={() => {
              set('imageMediaAssetId', null);
              setImageUrl(null);
            }}
          />

          <Field label={t('portalContent.prizes.fields.name')} error={errorFor('name')}>
            <input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              required
              maxLength={120}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('portalContent.prizes.fields.description')}
            error={errorFor('description')}
          >
            <textarea
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              rows={3}
              maxLength={500}
              className={inputClass}
            />
          </Field>

          <Field
            label={t('portalContent.prizes.fields.category')}
            error={errorFor('prizeCategoryId')}
          >
            <select
              value={form.prizeCategoryId}
              onChange={(event) => set('prizeCategoryId', event.target.value)}
              className={inputClass}
            >
              <option value="">{t('portalContent.prizes.noCategory')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={t('portalContent.prizes.fields.pointsCost')}
              error={errorFor('pointsCost')}
            >
              <input
                type="number"
                value={form.pointsCost}
                onChange={(event) => set('pointsCost', event.target.value)}
                required
                min={1}
                max={10_000_000}
                className={inputClass}
              />
            </Field>

            <Field
              label={t('portalContent.prizes.fields.stock')}
              hint={t('portalContent.prizes.stockHint')}
              error={errorFor('stock')}
            >
              <input
                type="number"
                value={form.stock}
                onChange={(event) => set('stock', event.target.value)}
                min={0}
                max={1_000_000}
                className={inputClass}
              />
            </Field>
          </div>

          <Field label={t('portalContent.prizes.fields.sortOrder')} error={errorFor('sortOrder')}>
            <input
              type="number"
              value={form.sortOrder}
              onChange={(event) => set('sortOrder', event.target.value)}
              min={0}
              max={9_999}
              className={inputClass}
            />
          </Field>

          <Toggle
            label={t('portalContent.prizes.fields.isActive')}
            checked={form.isActive}
            onChange={(value) => set('isActive', value)}
          />
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
